/**
 * Floating bottom-right tray that renders every active AI background job
 * from `AiJobsContext` — both `split` (lesson generation) and `quiz`
 * (auto-quiz generation) kinds. Stays visible across page navigation so
 * an admin can queue several chapters and still see every progress bar.
 */
import React, { useState } from "react";
import { useAiJobs } from "@/lib/ai-jobs";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
    Wand2, X, CheckCircle2, AlertCircle, Loader2,
    ChevronDown, ChevronUp, Sparkles, ScrollText, Brain, Film, Presentation,
} from "lucide-react";

const KIND_ICON = {
    split: ScrollText,
    quiz: Brain,
    video: Film,
    pptx: Presentation,
    chapter_pptx: Presentation,
};

function JobCard({ job, stageLabel, kindLabel, onDismiss, onCancel }) {
    const isDone = job.status === "done";
    const isFailed = job.status === "failed";
    const isRunning = !isDone && !isFailed;
    const pct = Math.round(Math.max(0, Math.min(100, job.percent || 0)));
    const KindIcon = KIND_ICON[job.kind] || Wand2;
    // How long has this job been running? Used to nudge the user toward
    // the Cancel button when something is clearly stuck (>= 5 min).
    const ageSec = job.startedAt ? Math.max(0, (Date.now() - job.startedAt) / 1000) : 0;
    const isStuck = isRunning && ageSec > 5 * 60;
    // Different sub-line under the chapter title per kind.
    let subline;
    if (job.kind === "split") {
        subline = `${kindLabel} · ${job.numLessons || job.total || "—"} lesson${(job.numLessons || job.total) === 1 ? "" : "s"}`;
    } else if (job.kind === "quiz") {
        subline = `${kindLabel} · 20 questions`;
    } else if (job.kind === "pptx") {
        subline = `${kindLabel} · PowerPoint deck`;
    } else if (job.kind === "chapter_pptx") {
        subline = `${kindLabel} · ${job.numLessons || "—"} lesson${job.numLessons === 1 ? "" : "s"}`;
    } else {
        // video
        subline = `${kindLabel} · ${job.total || "—"} slide${(job.total || 0) === 1 ? "" : "s"}`;
    }
    // Top line: lesson title for video/pptx jobs, chapter title for split/quiz/chapter_pptx.
    const headerTitle = (job.kind === "video" || job.kind === "pptx")
        ? (job.lessonTitle || job.chapterTitle || "Lesson")
        : (job.chapterTitle || "Chapter");

    return (
        <div
            className="rounded-md border border-border bg-card shadow-md px-3.5 py-3 space-y-2 animate-in fade-in slide-in-from-bottom-2"
            data-testid={`ai-job-card-${job.jobId}`}
        >
            <div className="flex items-start gap-2">
                <div className="mt-0.5 shrink-0">
                    {isRunning && (
                        <span
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/10"
                            data-testid="ai-job-icon-running"
                        >
                            <Loader2 className="h-4 w-4 animate-spin text-primary" />
                        </span>
                    )}
                    {isDone && (
                        <span
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500/15"
                            data-testid="ai-job-icon-done"
                        >
                            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        </span>
                    )}
                    {isFailed && (
                        <span
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-destructive/15"
                            data-testid="ai-job-icon-failed"
                        >
                            <AlertCircle className="h-4 w-4 text-destructive" />
                        </span>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1">
                        <KindIcon className="h-3 w-3" />
                        {subline}
                    </div>
                    <div className="text-sm font-medium truncate" title={headerTitle}>
                        {headerTitle}
                    </div>
                </div>
                {/* Single ✕ on every card. While running it cancels the
                    backend job (if the kind supports it) then drops the row
                    from the tray. When done/failed it just dismisses. */}
                <Button
                    size="icon"
                    variant="ghost"
                    className={`h-7 w-7 -mr-1 -mt-1 ${isRunning ? "text-muted-foreground hover:text-destructive" : ""}`}
                    onClick={isRunning && onCancel ? onCancel : onDismiss}
                    aria-label={isRunning ? "Cancel job" : "Dismiss"}
                    title={isRunning ? "Cancel this job" : "Dismiss"}
                    data-testid={`ai-job-${isRunning ? "cancel" : "dismiss"}-${job.jobId}`}
                >
                    <X className="h-4 w-4" />
                </Button>
            </div>

            {/* Animated progress bar — the shimmer overlay only shows while
                the job is running. */}
            <div className="relative">
                <Progress
                    value={pct}
                    className="h-2"
                    data-testid={`ai-job-progress-${job.jobId}`}
                />
                {isRunning && (
                    <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-full">
                        <div className="ai-progress-shimmer absolute inset-y-0 -left-1/3 w-1/3" />
                    </div>
                )}
            </div>

            <div className="flex items-center justify-between text-[11px] text-muted-foreground tabular-nums">
                <span className="truncate pr-2" data-testid={`ai-job-stage-${job.jobId}`}>
                    {stageLabel}
                </span>
                <span>{pct}%</span>
            </div>

            {isStuck && (
                <div className="flex items-start gap-1.5 text-[11px] text-amber-700 dark:text-amber-300">
                    <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>
                        Running for {Math.round(ageSec / 60)} min with little progress.
                        Click ✕ to cancel and try fewer concurrent jobs.
                    </span>
                </div>
            )}

            {isDone && (job.partial_failures || 0) > 0 && (
                <div className="flex items-start gap-1.5 text-[11px] text-amber-700 dark:text-amber-300">
                    <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>
                        Partial: {job.partial_failures} lesson{job.partial_failures === 1 ? "" : "s"} could not be drafted. Re-run on the chapter to fill in the rest.
                    </span>
                </div>
            )}
        </div>
    );
}

export default function AiJobsTray() {
    const { jobs, dismissJob, activeCount, getStageLabel, getKindLabel } = useAiJobs();
    const { user } = useAuth();
    const [collapsed, setCollapsed] = useState(false);
    const [cancellingAll, setCancellingAll] = useState(false);
    // Never render on unauthenticated pages (login, signup, public marketing
    // home). Background-job status endpoints all require an auth token, so
    // an unauthenticated tray could only ever show stale data — and it was
    // covering the "Sign In" button on the login form.
    if (!user) return null;
    const list = Object.values(jobs).sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0));
    if (!list.length) return null;

    const doneCount = list.filter((j) => j.status === "done").length;

    // Cancel/clear a job. For video & pptx kinds we hit a backend cancel
    // endpoint so the DB row flips to `failed`. For split & quiz (which
    // are short-lived and don't have a kill switch in the AI SDK) we
    // simply drop the entry from the tray — the worker finishes on its
    // own server-side. Either way, the row disappears from the user's
    // view immediately.
    const handleCancel = async (job) => {
        try {
            if (job.kind === "video") {
                await api.post(`/lesson-video-jobs/${job.jobId}/cancel`);
            } else if (job.kind === "pptx") {
                await api.post(`/lesson-pptx-jobs/${job.jobId}/cancel`);
            } else if (job.kind === "chapter_pptx") {
                await api.post(`/chapter-pptx-jobs/${job.jobId}/cancel`);
            }
            // split/quiz have no server-side cancel — just drop the row.
        } catch {
            /* best-effort — even if backend cancel fails, drop the tray entry */
        } finally {
            dismissJob(job.jobId);
        }
    };

    // Cancel every running/queued job in one click. Issues all backend
    // cancel calls in parallel for the kinds that support them, then drops
    // every row from the tray regardless. After this runs the tray is
    // empty and the user can re-queue from scratch.
    const handleCancelAll = async (e) => {
        // Stop the click from also toggling the tray collapse state.
        e.stopPropagation();
        const running = list.filter(
            (j) => j.status === "running" || j.status === "queued",
        );
        if (!running.length) return;
        setCancellingAll(true);
        try {
            await Promise.all(running.map((j) => handleCancel(j)));
        } finally {
            setCancellingAll(false);
        }
    };

    return (
        <div
            className="fixed z-50 bottom-4 right-4 w-[min(380px,calc(100vw-2rem))]"
            data-testid="ai-jobs-tray"
        >
            <div className="rounded-md border border-border bg-background/95 backdrop-blur shadow-xl overflow-hidden">
                <div className="w-full flex items-center gap-2 px-3.5 py-2.5 bg-card text-left">
                    <button
                        type="button"
                        onClick={() => setCollapsed((v) => !v)}
                        className="flex items-center gap-2 flex-1 min-w-0 hover:opacity-80 transition-opacity"
                        data-testid="ai-jobs-tray-toggle"
                    >
                        <Wand2 className="h-4 w-4 text-primary shrink-0" />
                        <div className="flex-1 min-w-0 text-left">
                            <div className="text-sm font-semibold leading-none flex items-center gap-1.5">
                                AI jobs
                                {activeCount > 0 && (
                                    <Sparkles className="h-3.5 w-3.5 text-primary animate-pulse" />
                                )}
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">
                                {activeCount > 0
                                    ? `${activeCount} running${doneCount ? ` · ${doneCount} done` : ""}`
                                    : `${list.length} complete`}
                            </div>
                        </div>
                    </button>
                    {activeCount >= 2 && (
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleCancelAll}
                            disabled={cancellingAll}
                            className="h-7 px-2 text-[11px] text-muted-foreground hover:text-destructive hover:bg-destructive/10 gap-1"
                            data-testid="ai-jobs-cancel-all"
                            title={`Cancel all ${activeCount} running jobs`}
                        >
                            {cancellingAll ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                                <X className="h-3 w-3" />
                            )}
                            Cancel all
                        </Button>
                    )}
                    <button
                        type="button"
                        onClick={() => setCollapsed((v) => !v)}
                        aria-label={collapsed ? "Expand AI jobs" : "Collapse AI jobs"}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                        {collapsed ? (
                            <ChevronUp className="h-4 w-4" />
                        ) : (
                            <ChevronDown className="h-4 w-4" />
                        )}
                    </button>
                </div>
                {!collapsed && (
                    <div
                        className="p-2.5 space-y-2 max-h-[60vh] overflow-y-auto"
                        data-testid="ai-jobs-tray-body"
                    >
                        {list.map((job) => (
                            <JobCard
                                key={job.jobId}
                                job={job}
                                stageLabel={getStageLabel(job)}
                                kindLabel={getKindLabel(job)}
                                onDismiss={() => dismissJob(job.jobId)}
                                onCancel={() => handleCancel(job)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
